"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Reading progress for long-form articles only (#main-article).
 * Throttled with rAF; 100% when the bottom of the article enters the viewport.
 */
export function BlogArticleScrollProgress() {
  const [pct, setPct] = useState(0);
  const [active, setActive] = useState(false);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const el = document.getElementById("main-article");
    if (!el) return;
    setActive(true);

    const tick = () => {
      raf.current = null;
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const vh = window.innerHeight;
      const rect = el.getBoundingClientRect();
      const top = rect.top + scrollY;
      const h = el.offsetHeight;
      const travel = Math.max(h - vh, 1);
      let p = 0;
      if (scrollY <= top) p = 0;
      else if (scrollY >= top + travel) p = 100;
      else p = Math.round(((scrollY - top) / travel) * 100);
      setPct(Math.min(100, Math.max(0, p)));
    };

    const onScroll = () => {
      if (raf.current != null) return;
      raf.current = window.requestAnimationFrame(tick);
    };

    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf.current != null) window.cancelAnimationFrame(raf.current);
    };
  }, []);

  if (!active) return null;

  return (
    <div
      className="ed-blog-scroll-progress-article"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label="Article reading progress"
    >
      <div className="ed-blog-scroll-progress-article__fill" style={{ transform: `scaleX(${pct / 100})` }} />
    </div>
  );
}
